import type { MiMesa, Silla } from '../datos/index.js'
import { conSigno, signoDe } from '../formato.js'
import { Bandera } from './Bandera.js'
import { Icono } from './Icono.js'
import { iniciales } from './MesaVs.js'
import { PastillaResultado } from './Pastilla.js'

/**
 * Una mesa vista desde la silla del que mira.
 *
 * Es el objeto central de toda la web: la pregunta que trae al jugador a la
 * página es «¿en qué mesa voy y con quién?», y la respuesta a esa pregunta es
 * exactamente esta tarjeta. Sirve igual para la que está por jugar y para las
 * que ya jugó —son la misma cosa en dos momentos— y lo único que cambia es si
 * hay marcador.
 *
 * No dibuja los dos bandos enfrentados como la app de escritorio. Allá la mesa
 * se mira desde arriba, para reconocerla en el salón; acá se mira desde una de
 * las cuatro sillas, y lo que hay que distinguir de un vistazo no es quién está
 * a la izquierda sino quién juega conmigo y quién en contra. Por eso los
 * títulos son «Contigo» y «En contra» y no «Bando 1» y «Bando 2».
 *
 * Arriba de los nombres va el duelo: los dos escudos de iniciales, el propio a
 * la izquierda, y el marcador grande en el medio. Es lo primero que se lee y lo
 * que se mira de lejos con el teléfono apoyado en la mesa: cómo vamos, sin
 * buscar el número en un renglón.
 */

function Nombre({ silla }: { silla: Silla }) {
  return (
    <span className={`silla ${silla.esMio ? 'mia' : ''}`}>
      <Bandera codigo={silla.pais} />
      {silla.numero !== null && <span className="silla-numero">{silla.numero}</span>}
      <span className="silla-nombre">{silla.nombre}</span>
      {silla.esMio && <span className="silla-tu">tú</span>}
    </span>
  )
}

/** Los círculos de un bando, como el escudo de un equipo en una transmisión. */
function Escudo({ sillas, propio }: { sillas: Silla[]; propio: boolean }) {
  return (
    <span className={`duelo-escudo ${propio ? 'propio' : ''}`} aria-hidden="true">
      {sillas.map((s) => (
        <span key={s.participanteId} className={`duelo-avatar ${s.esMio ? 'yo' : ''}`}>
          {iniciales(s.nombre)}
        </span>
      ))}
    </span>
  )
}

/**
 * Qué número va en el medio del duelo.
 *
 * El resultado si la partida terminó jugándose; el parcial de la planilla si se
 * está jugando y el torneo la usa; y si no hay ninguno de los dos, nada: el
 * «V/S» dice que la partida está por empezar mejor que un cero a cero.
 */
function cifrasDelDuelo(mesa: MiMesa): { propios: number; rivales: number; etiqueta: string } | null {
  const marcador = mesa.marcador
  if (marcador) {
    if (marcador.motivo !== 'normal') return null
    return { propios: marcador.puntosPropios, rivales: marcador.puntosRivales, etiqueta: 'Final' }
  }
  const parcial = mesa.parcial
  if (parcial && mesa.miBando !== null && parcial.manos > 0) {
    const propios = mesa.miBando === 1 ? parcial.bando1 : parcial.bando2
    const rivales = mesa.miBando === 1 ? parcial.bando2 : parcial.bando1
    return { propios, rivales, etiqueta: `Mano ${parcial.manos}` }
  }
  return null
}

export function MesaDelJugador({
  mesa,
  destacada = false,
  alAnotar,
}: {
  mesa: MiMesa
  /** La mesa que está por jugarse: la web entera existe para mostrar esta. */
  destacada?: boolean
  /**
   * Abre la libreta de anotación. Solo en la mesa que se está jugando y en los
   * torneos que la usan; sin esto la tarjeta no ofrece anotar.
   */
  alAnotar?: () => void
}) {
  const marcador = mesa.marcador
  const jugada = marcador !== null && marcador.motivo === 'normal'

  return (
    <article className={`mesa ${destacada ? 'destacada' : ''}`}>
      <header className="mesa-cabecera">
        <span className="mesa-ronda">Ronda {mesa.rondaNumero}</span>
        {mesa.tipo === 'incompleta' ? (
          <span className="mesa-numero sin-mesa">Sin mesa</span>
        ) : (
          <span className="mesa-numero">
            Mesa <b>{mesa.mesaNumero}</b>
          </span>
        )}
        {marcador && (
          <PastillaResultado resultado={marcador.resultado} motivo={marcador.motivo} />
        )}
        {!marcador &&
          (alAnotar ? (
            <button className="pastilla vivo mesa-anotar-pastilla" onClick={alAnotar}>
              Anotar
              <Icono nombre="flecha" tamano={13} />
            </button>
          ) : (
            <span className="pastilla vivo">A jugar</span>
          ))}
      </header>

      {mesa.tipo === 'incompleta' ? (
        /* La mesa incompleta no tiene bandos: los que sobran del armado no se
           sientan contra nadie. Explicarlo es media razón para que la tarjeta
           exista — el que la recibe cree que se lo saltearon. */
        <div className="mesa-bye">
          <p className="mesa-bye-texto">
            Esta ronda te tocó <b>descansar</b>: quedaron menos de cuatro jugadores libres y la
            mesa no se pudo completar. No se juega, y se te acreditan{' '}
            <b>{marcador?.puntosPropios ?? 0} puntos</b> sin partida.
          </p>
        </div>
      ) : (
        <Duelo mesa={mesa} />
      )}

      {mesa.tipo !== 'incompleta' && (
        <div className="mesa-lados">
          <div className="lado">
            <h4>Contigo</h4>
            <ul>
              <li>
                <Nombre silla={mesa.yo} />
              </li>
              {mesa.companeros.map((c) => (
                <li key={c.participanteId}>
                  <Nombre silla={c} />
                </li>
              ))}
            </ul>
          </div>

          <div className="lado-separador" aria-hidden="true">
            <span>vs</span>
          </div>

          <div className="lado">
            <h4>En contra</h4>
            <ul>
              {mesa.rivales.map((r) => (
                <li key={r.participanteId}>
                  <Nombre silla={r} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {jugada && marcador && (
        <footer className="mesa-marcador">
          {/* La efectividad va pegada a la tarjeta del resultado y no en otra
              pantalla porque es el número que no cierra solo: quien ganó 215 a
              180 suma 20 y no 35, y sin verlo junto al marcador parece un error
              de la tabla. */}
          <span className={`mesa-efectividad ${signoDe(marcador.efectividad)}`}>
            {conSigno(marcador.efectividad)} de efectividad
          </span>
        </footer>
      )}

      {!marcador && mesa.tipo !== 'incompleta' && (
        <footer className="mesa-pendiente">
          <span>Se juega a {mesa.objetivoPuntos} puntos</span>
          {/* El mismo botón que arriba, donde cae el pulgar. */}
          {alAnotar && (
            <button className="boton principal mesa-anotar" onClick={alAnotar}>
              Abrir la libreta de anotación
            </button>
          )}
        </footer>
      )}
    </article>
  )
}

function Duelo({ mesa }: { mesa: MiMesa }) {
  const cifras = cifrasDelDuelo(mesa)
  const gano = mesa.marcador?.resultado === 'ganada'
  const perdio = mesa.marcador?.resultado === 'perdida'
  /* La barra de avance solo mientras se juega: con la partida cerrada ya no
     falta nada, y lo que se lee es quién ganó. */
  const enJuego = cifras !== null && mesa.marcador === null

  return (
    <div className={`mesa-duelo ${enJuego ? 'en-juego' : ''}`}>
      <Escudo sillas={[mesa.yo, ...mesa.companeros]} propio />
      <div className="duelo-centro">
        {cifras ? (
          <>
            <span className="duelo-etiqueta">{cifras.etiqueta}</span>
            <span
              className="duelo-cifras"
              aria-label={`${cifras.propios} a ${cifras.rivales}`}
            >
              <b className={perdio ? 'atras' : ''}>{cifras.propios}</b>
              <span aria-hidden="true">:</span>
              <b className={gano ? 'atras' : ''}>{cifras.rivales}</b>
            </span>
          </>
        ) : (
          <span className="duelo-vs" aria-hidden="true">
            V<span>/</span>S
          </span>
        )}
        <span className="duelo-objetivo">a {mesa.objetivoPuntos}</span>
      </div>
      <Escudo sillas={mesa.rivales} propio={false} />
      {enJuego && cifras && (
        <span className="duelo-avance" aria-hidden="true">
          <i style={{ width: `${Math.min(100, (cifras.propios / mesa.objetivoPuntos) * 100)}%` }} />
        </span>
      )}
    </div>
  )
}
